import { memo, useEffect, useState } from "react";
import styled from "styled-components";

import { IAccountInfo, ICountryData } from "@interfaces";
import { IHandleBlur, IHandleChange, IHandleSubmit } from "@interfaces";
import { InputCommon, SelectCommon, StyledButton } from "@components";
import { breakpoint, theme } from "@utilities";
import { externalApi } from "@api";
import { useField, useFormikContext } from "formik";
import { TextField } from "@mui/material";
import { SeparateDatePicker } from "../Common/SeparateDatePicker";
import { toCountryOptions } from "src/Utilities/countryOrder";
interface IProps {
    values: IAccountInfo;
    handleChange: IHandleChange;
    handleBlur: IHandleBlur;
    handleSubmit: IHandleSubmit;
    countriesData: ICountryData[];
    isUpdateAccount: boolean;
    isLeader?: boolean;
    isSubmitting: boolean;
    errors?: { [key: string]: string };
    touched?: { [key: string]: boolean };
}

export const AccountComponent = memo((props: IProps) => {
    const [countriesData, setCountriesData] = useState<ICountryData[]>([]);
    useEffect(() => {
        (async () => {
            const res = await externalApi.getCountriesDate();
            setCountriesData(res.data);
        })();
    }, []);

    const countriesModel = toCountryOptions(countriesData);
    const { values, handleChange, handleSubmit, handleBlur, isUpdateAccount, isSubmitting } = props;
    const { setFieldValue } = useFormikContext();

    const raw = values.phone_number ?? "";
    const digitCount = (raw.match(/\d/g) || []).length;
    const isTooShort = digitCount > 0 && digitCount < 4;

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.ctrlKey || e.metaKey) return;
        if (["Backspace", "Delete", "ArrowLeft", "ArrowRight", "Tab"].includes(e.key)) return;
        if (!/[\d()+\-\s]/.test(e.key)) e.preventDefault();
    };
    const sanitize = (str: string) => str.replace(/[^0-9()+\-\s]/g, "");

    const handlePhonePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
        window.requestAnimationFrame(() => {
            const pasted = (e.target as HTMLInputElement).value;
            handleChange(sanitize(pasted));
        });
    };
    const [, phoneMeta] = useField(`phone_number`);
    const [, dobMeta] = useField(`date_of_birth`);

    const TitleModel = [
        { id: "Mr", label: "Mr" },
        { id: "Mrs", label: "Mrs" },
        { id: "Miss", label: "Miss" },
        { id: "Ms", label: "Ms" },
        { id: "Mx", label: "Mx" },
        { id: "Master", label: "Master" },
    ];
    const handleFormSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        // Check phone number validation before submission
        const digitCount = ((values.phone_number ?? "").match(/\d/g) || []).length;
        if (digitCount < 4) {
            // You can show a toast/alert here or set form errors
            console.error("Phone number must have at least 4 digits");
            return; // Prevent form submission
        }

        handleSubmit(e); // Call original submit handler
    };
    return (
        <Container onSubmit={handleFormSubmit}>
            <h2 className="title__account">
                {isUpdateAccount ? "Update My Account Details" : "Account Details"}
            </h2>
            {isUpdateAccount && values.email && (
                <EmailDisplay>
                    <span className="email-label">Email</span>
                    <span className="email-value">{values.email}</span>
                </EmailDisplay>
            )}
            <div className="Wrapper">
                <div style={{ width: "100%" }}>
                    <SelectCommon
                        name={`title`}
                        handleChange={(value) => setFieldValue(`title`, value)}
                        handleBlur={handleBlur}
                        value={values.title}
                        label="Title"
                        required
                        listValueMembers={TitleModel}
                    />
                </div>
                <div className="col_48">
                    <InputCommon
                        name="first_name"
                        handleChange={handleChange}
                        handleBlur={handleBlur}
                        value={values.first_name}
                        label="First name *"
                        type="text"
                        required
                    />
                </div>
                <div className="col_48">
                    <InputCommon
                        name="last_name"
                        handleChange={handleChange}
                        handleBlur={handleBlur}
                        required
                        value={values.last_name}
                        label="Last name *"
                        type="text"
                    />
                </div>
            </div>

            <div className="Wrapper">
                <div className="col_48 m_100">
                    <SeparateDatePicker
                        value={values.date_of_birth ? new Date(values.date_of_birth) : null}
                        onChange={(date) => setFieldValue("date_of_birth", date)}
                        label="Date of birth *"
                        name="date_of_birth"
                        maxDate={new Date()}
                        minDate={new Date("1/1/1900")}
                    />
                    {dobMeta.touched && dobMeta.error && (
                        <div style={{ color: "red", fontSize: "12px", marginTop: "14px" }}>
                            {dobMeta.error}
                        </div>
                    )}
                </div>
                <div className="col_48 m_100">
                    <InputCommon
                        name="post_code"
                        handleChange={handleChange}
                        handleBlur={handleBlur}
                        value={values.post_code}
                        label="Post Code *"
                        required
                        type="text"
                    />
                </div>
            </div>

            <div className="Wrapper">
                <div className="col_48 m_100">
                    <TextField
                        style={{ fontWeight: "bold !important" }}
                        name="phone_number"
                        label="Phone Number *"
                        type="tel"
                        className="phone_number"
                        placeholder="+1 (234) 567-8900"
                        fullWidth
                        value={raw}
                        variant="standard"
                        error={isTooShort}
                        helperText={isTooShort ? "Please enter at least 4 digits." : ""}
                        onBlur={handleBlur}
                        onKeyDown={handleKeyDown}
                        onChange={handleChange}
                        sx={{
                            "& .MuiInput-underline:before": {
                                borderBottom: "none",
                            },
                            "& .MuiInput-underline:after": {
                                borderBottom: "none",
                            },
                            "& .MuiInput-underline:hover:not(.Mui-disabled):before": {
                                borderBottom: "none",
                            },
                            "& .MuiInputLabel-root.Mui-focused": {
                                color: theme.colors.highlight,
                            },
                            "& .MuiFormHelperText-root": {
                                marginTop: "10px",
                            },
                        }}
                        slotProps={{
                            htmlInput: {
                                inputMode: "tel",
                                pattern: `(?=(?:.*\\d){4,})[0-9()+\\-\\s]+`,
                                title: "Enter at least 4 digits; only digits, spaces, (), +, - allowed",
                                onPaste: handlePhonePaste,
                            },
                            inputLabel: {
                                sx: {
                                    "&.MuiInputLabel-shrink": {
                                        fontWeight: "bold",
                                    },
                                },
                            },
                        }}
                    />
                    <hr style={{ marginTop: "-9px", marginBottom: "3px" }} />
                    {phoneMeta.touched && phoneMeta.error && (
                        <div style={{ color: "red", fontSize: "12px", marginTop: "14px" }}>
                            {phoneMeta.error}
                        </div>
                    )}
                </div>
                <div className="col_48 m_100">
                    <SelectCommon
                        name="country_code"
                        handleChange={(value) => setFieldValue("country_code", value)}
                        handleBlur={handleBlur}
                        value={values.country_code}
                        label="Country"
                        required
                        listValueMembers={countriesModel}
                    />
                </div>
            </div>

            <StyledButton
                $borderRadius={theme.borderRadius.small}
                color={theme.gradients.button}
                className="submit_account"
                disabled={isSubmitting}
                type="submit"
            >
                {isUpdateAccount ? "Update Details" : "Submit"}
            </StyledButton>
            <div className="note">
                <span>
                    *To change your email address please contact{" "}
                    <a href="mailto:support@freelancetravel.com">support@freelancetravel.com</a>.
                    Please note this will also update your account login details.
                </span>
            </div>
        </Container>
    );
});

const EmailDisplay = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
    margin-bottom: 12px;
    padding: 8px 0;

    .email-label {
        font-size: 12px;
        font-weight: bold;
        color: #666;
    }

    .email-value {
        font-size: 14px;
        color: #333;
    }
`;

const Container = styled.form`
    .note {
        color: ${(props) => props.theme.colors.gray_3};
        font-size: 14px;
        margin-top: 1em;
        line-height: 1.25em;
        /* Grey with an underline, as in the cancellation popup: the address sits
           mid-sentence, so it reads as part of it and the underline marks it as a
           link. */
        a {
            color: #747474;
            text-decoration: underline;
        }
    }

    .Wrapper {
        width: 100%;
        display: flex;
        justify-content: space-between;
        flex-wrap: wrap;
        .col_48 {
            width: 48%;
        }
        ${breakpoint.breakOnlyMobile`
        flex-direction : column;
        .col_48 {
            width: 100%;
        }
        .col_48.m_100 {
            width: 100%;
         }
        `};
    }
    .submit_account {
        width: 40%;
        margin: auto;
        margin-top: 50px;
        ${breakpoint.breakOnlyMobile`
        width: 100%;
        margin-top: 30px;
        `};
    }
    .MuiFormControl-root {
        height: 65px;
        margin: 0 !important;
    }
    .title__account {
        font-weight: bold;
        margin-bottom: 12px !important;
    }
    .MuiFormControl-root .gFTEKm {
        margin-top: 0 !important;
    }
    label {
        display: -webkit-box;
        -webkit-line-clamp: 1;
        -webkit-box-orient: vertical;
        overflow: hidden;
    }
`;
